import { AddPhotoComponent } from '@app/patients/patient-tabs/patient-photos-tab/add-photo/add-photo.component';
import { cu } from '@fullcalendar/core/internal-common';
import { PhotoMetaData } from '@models/photo/photo-meta-data';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Components } from 'formiojs';
import baseEditForm from 'formiojs/components/_classes/component/Component.form';
export class SketchpadComponent extends Components.components.field {
  // Required base properties
  root!: any;
  options!: any;
  component!: any;

  canvas!: HTMLCanvasElement;
  ctx!: CanvasRenderingContext2D;
  wrapper!: HTMLDivElement;
  toolbar!: HTMLDivElement;
  colorInput!: HTMLInputElement;
  sizeInput!: HTMLInputElement;
  sizeLabel!: HTMLSpanElement;
  clearBtn!: HTMLButtonElement;
  undoBtn!: HTMLButtonElement;
  redoBtn!: HTMLButtonElement;
  input!: HTMLInputElement;

  backgroundImage!: HTMLImageElement;
  backgroundLoaded: boolean = false;

  paths: any[] = [];
  undoStack: any[] = [];
  redoStack: any[] = [];
  currentPath: any = null;
  drawing: boolean = false;

  /** Suppress change events during programmatic updates */
  suppressChange: boolean = false;
  // Angular service is stored locally, not in the schema
  private _modalService?: NgbModal;

  constructor(component: any, options: Object, data: Object) {
    super(component, options, data);
  }

  get isBuilderMode(): boolean {
    return !!(this.options && this.options.builder);
  }

  setModalService(modalService: NgbModal) {
    this._modalService = modalService;
  }

  get modalService(): NgbModal | null {
    return this._modalService ?? null;
  }

  static schema(extend: any = {}) {
    return {
      type: 'sketchpad',
      label: 'Sketchpad',
      key: 'sketchpad',
      input: true,
      tableView: false,
      background: '',
      penColor: '#000000',
      penSize: 4,
      thumbnailPreview: '',
      ...extend,
    };
  }

  static get builderInfo() {
    return {
      title: 'Sketchpad',
      group: 'basic',
      icon: 'paint-brush',
      weight: 70,
      schema: SketchpadComponent.schema(),
    };
  }

  static editForm = (...extend: any[]) => {
    const form = baseEditForm(
      [
        {
          key: 'display',
          components: [
            {
              type: 'htmlelement',
              key: 'selectBtn',
              tag: 'div',
              customClass: 'form-group mt-4',
              content: `
              <label>Background Image</label>
              <div>
                <button class="btn btn-sm btn-primary select-background-btn" type="button">
                  Choose from Clinic Photos
                </button>
              </div>
            `,
            },
            {
              type: 'htmlelement',
              key: 'thumbnailPreview',
              tag: 'div',
              customClass: 'form-group',
              content: `<span class="no-background-msg" style="color: #999; font-style: italic; {{ data.background ? 'display:none;' : '' }}">No background selected</span>
                <img class="background-thumbnail" src="{{ data.background || \'\' }}" style="max-width: 100%; max-height: 100px; margin-top: 5px;border: 1px solid #ddd; border-radius: 4px; background: #f8f9fa;" />`,
            },
            {
              type: 'textfield',
              key: 'background',
              label: 'Background Image URL',
              input: true,
              disabled: true,
              placeholder: 'No background image selected',
              customClass: 'form-control-plaintext text-muted sketchpad-background-input',
              tooltip: 'The currently selected background image file path.',
            },
            {
              type: 'htmlelement',
              key: 'penColorDisplay',
              tag: 'div',
              label: 'Default Pen Color',
              content: `
                    <label>Default Pen Color</label>
                    <div class="d-flex align-items-center gap-2" style="margin-top: 3px;">
                      <input type="color"
                            class="form-control form-control-color pen-color-display-input"
                            value="{{ data.penColor || '#000000' }}"            
                            style="width: 50px; height: 32px; padding: 0; border: none;" />                    
                    </div>
                  `,
            },
            {
              type: 'textfield',
              key: 'penColor',
              label: 'Default Pen Color',
              input: true,
              disabled: true,
              placeholder: '#000000',
            },
            { type: 'number', key: 'penSize', label: 'Default Pen Size', input: true, placeholder: '4' },
          ],
        },
      ],
      ...extend
    );

    return form;
  };

  render(children?: any) {
    return super.render(`
      <div class="sketchpad-wrapper" style="display:flex; flex-direction:column; align-items:center; gap:6px;">
        <div class="sketchpad-toolbar" style="display:flex; gap:8px; align-items:center;"></div>
        <canvas class="sketchpad-canvas" style="border:1px solid #ccc; width:100%; height:auto;"></canvas>
        <input ref="hiddenInput" type="hidden" />
      </div>
    `);
  }

  attach(element: HTMLElement) {
    if ((element as any)._sketchpadAttached) return; // already attached
    (element as any)._sketchpadAttached = true;

    const superResult = super.attach(element);

    // inject Angular service at runtime
    if (!this.modalService && window['ngbModalInstance']) {
      this.setModalService(window['ngbModalInstance']);
    }

    this.wrapper = element.querySelector('.sketchpad-wrapper') as HTMLDivElement;

    this.toolbar = this.wrapper.querySelector('.sketchpad-toolbar') as HTMLDivElement;
    this.canvas = this.wrapper.querySelector('.sketchpad-canvas') as HTMLCanvasElement;
    this.ctx = this.canvas.getContext('2d')!;

    // Toolbar
    this.colorInput = document.createElement('input');
    this.colorInput.type = 'color';
    this.colorInput.value = this.component.penColor || '#000000';

    this.sizeInput = document.createElement('input');
    this.sizeInput.type = 'range';
    this.sizeInput.min = '1';
    this.sizeInput.max = '30';
    this.sizeInput.value = String(this.component.penSize || 4);

    this.sizeLabel = document.createElement('span');
    this.sizeLabel.textContent = 'Size: ' + (this.component.penSize || 4);

    this.clearBtn = document.createElement('button');
    this.clearBtn.type = 'button';
    this.clearBtn.textContent = 'Clear';

    this.undoBtn = document.createElement('button');
    this.undoBtn.type = 'button';
    this.undoBtn.textContent = 'Undo';

    this.redoBtn = document.createElement('button');
    this.redoBtn.type = 'button';
    this.redoBtn.textContent = 'Redo';

    this.toolbar.append(this.colorInput, this.sizeLabel, this.sizeInput, this.clearBtn, this.undoBtn, this.redoBtn);

    // Hidden input
    this.input = document.createElement('input');
    this.input.type = 'hidden';
    this.wrapper.appendChild(this.input);

    // Restore background if set
    if (this.component.background) this.loadBackground(this.component.background);

    // Restore strokes if any
    if (this.dataValue && typeof this.dataValue === 'object' && Array.isArray(this.dataValue.paths)) {
      this.suppressChange = true;
      this.paths = this.dataValue.paths;
      this.suppressChange = false;
    }

    // Event listeners
    this.canvas.addEventListener('pointerdown', this.onPointerDown.bind(this));
    window.addEventListener('pointerup', this.onPointerUp.bind(this));
    this.canvas.addEventListener('pointermove', this.onPointerMove.bind(this));
    this.sizeInput.addEventListener('input', this.onSizeChange.bind(this));
    this.colorInput.addEventListener('input', () => this.handlePenColorChange(this.colorInput.value));

    this.clearBtn.addEventListener('click', this.onClear.bind(this));
    this.undoBtn.addEventListener('click', this.onUndo.bind(this));
    this.redoBtn.addEventListener('click', this.onRedo.bind(this));

    this.setCanvasWidthAndScale();
    window.addEventListener('resize', () => this.setCanvasWidthAndScale());

    return superResult;
  }

  afterEditForm(editForm) {
    // Wait for inputs to exist in DOM
    setTimeout(() => {
      // Select background button event handler
      const bgBtn = editForm.element.querySelector('.select-background-btn') as HTMLButtonElement;
      if (bgBtn && !bgBtn.dataset.listenerAttached) {
        bgBtn.dataset.listenerAttached = 'true';
        bgBtn.removeEventListener('click', this.handleChooseBackgroundClick);
        bgBtn.addEventListener('click', async () => this.handleChooseBackgroundClick());
      }

      // Pen color input handler
      const penColorDisplayInput = editForm.element.querySelector('.pen-color-display-input');
      const penColorInput = editForm.element.querySelector('input[name="data[penColor]"]');

      if (penColorDisplayInput && !penColorDisplayInput.dataset.listenerAttached) {
        penColorDisplayInput.dataset.listenerAttached = 'true';
        penColorDisplayInput.addEventListener('input', (e) => {
          const newColor = (e.target as HTMLInputElement).value;
          this.component.penColor = newColor;
          if (penColorInput) penColorInput.value = newColor;

          // Trigger native input event
          const event = new Event('input', { bubbles: true, cancelable: true });
          penColorInput.dispatchEvent(event);
          // Optionally trigger change event too (some handlers listen for 'change')
          const changeEvent = new Event('change', { bubbles: true, cancelable: true });
          penColorInput.dispatchEvent(changeEvent);
        });
      }

      if (penColorInput && !penColorInput.dataset.listenerAttached) {
        penColorInput.dataset.listenerAttached = 'true';
        penColorInput.addEventListener('input', () => {
          this.component.penColor = penColorInput.value;
          if (this.colorInput) this.colorInput.value = this.component.penColor;
          this.updateValue(true);
        });
      }

      // Background input handler
      const backgroundInput = editForm.element.querySelector('input[name="data[background]"]');
      if (backgroundInput && !backgroundInput.dataset.listenerAttached) {
        backgroundInput.dataset.listenerAttached = 'true';
        backgroundInput.addEventListener('input', () => {
          this.component.background = backgroundInput.value;
          this.loadBackground(this.component.background);
          this.updateValue(true);
        });
      }
    }, 200); // allow modal DOM to render

    editForm.on('change', (event) => {
      if (!event.changed?.component?.key) return;

      const key = event.changed.component.key;
      const value = event.changed.value;

      if (key === 'penColor') {
        this.component.penColor = value;
        this.updateValue(true);
      }

      if (key === 'background') {
        this.component.background = value;
        this.updateValue(true);
      }
    });
  }

  private handleChooseBackgroundClick = async () => {
    const imageUrl = await this.onAddPhoto();
    if (!imageUrl) return;

    const wrapper = this.element.closest('.component-edit-tabs') || document;
    if (!wrapper) return;

    // Update component and canvas
    this.component.background = imageUrl;
    this.loadBackground(imageUrl);

    // Query inputs inside the wrapper
    const bgInput = wrapper.querySelector('input[name="data[background]"]') as HTMLInputElement;
    const bgPreview = wrapper.querySelector('.background-thumbnail') as HTMLImageElement;
    const noBgMsg = wrapper.querySelector('.no-background-msg') as HTMLElement;

    if (bgPreview) bgPreview.src = imageUrl;
    if (noBgMsg) noBgMsg.style.display = 'none';
    //const photoPathWithoutSAS = imageUrl.split('?')[0];
    if (bgInput) bgInput.value = imageUrl;

    // Trigger native input event
    const event = new Event('input', { bubbles: true, cancelable: true });
    bgInput.dispatchEvent(event);
    // Optionally trigger change event too (some handlers listen for 'change')
    const changeEvent = new Event('change', { bubbles: true, cancelable: true });
    bgInput.dispatchEvent(changeEvent);
  };

  private handlePenColorChange(color: string) {
    this.component.penColor = color || '#000000';
    this.colorInput.value = this.component.penColor;
    if (this.ctx) this.ctx.strokeStyle = this.component.penColor;

    // const penColorDisplay = this.wrapper.querySelector<HTMLInputElement>('.pen-color-display-input');
    // const penColorInput = this.wrapper.querySelector<HTMLInputElement>('input[name="data[penColor]"]');

    // if (penColorInput) penColorInput.value = this.component.penColor;
    // if (penColorDisplay) penColorDisplay.value = this.component.penColor;
  }

  async onAddPhoto(): Promise<string | null> {
    const modal = this.modalService;
    if (!modal) {
      console.warn('modalService not available in SketchpadComponent');
      return null;
    }

    try {
      const modalRef = this.modalService.open(AddPhotoComponent, {
        windowClass: 'add-photo-modal',
        centered: true,
      });
      // Get the topmost modal element (the actual dialog)
      const modalEl = document.querySelector('ngb-modal-window') as HTMLElement;
      if (modalEl) modalEl.style.zIndex = '10020';
      const backdrop = document.querySelector('ngb-modal-backdrop') as HTMLElement;
      if (backdrop) backdrop.style.zIndex = '10010';

      const instance = modalRef.componentInstance as AddPhotoComponent;
      instance.isClinicPhoto = true;
      instance.showGallery = true;
      instance.singleImageSelect = true;

      const result: PhotoMetaData = await modalRef.result;
      //const photoPathWithoutSAS = result.filePath.split('?')[0];
      return result.filePath;
    } catch {
      return null; // user closed the modal
    }
  }

  /** --- Value handling --- */
  updateValue(userInitiated: boolean = true) {
    // Ensure we only have a boolean
    const isUserInitiated = typeof userInitiated === 'boolean' ? userInitiated : false; // default to false if it's not boolean

    const displayWidth = this.canvas.offsetWidth || this.canvas.width / (window.devicePixelRatio || 1);
    const displayHeight = this.canvas.offsetHeight || this.canvas.height / (window.devicePixelRatio || 1);

    const value = {
      background: this.component.background || '',
      paths: this.paths,
      canvasWidth: displayWidth,
      canvasHeight: displayHeight,
    };
    this.dataValue = value;
    this.input.value = JSON.stringify(value);

    if (isUserInitiated && !this.suppressChange) {
      this.updateOnChange({ changed: true, isModified: true }, true);
    }
  }

  setValue(value: any, flags?: any): boolean {
    this.suppressChange = true; // Suppress all change events

    if (!value) value = { paths: [] };
    if (typeof value === 'string') {
      try {
        value = JSON.parse(value);
      } catch {
        value = { paths: [] };
      }
    }

    this.dataValue = value;
    // Restore paths
    if (value.paths) this.paths = value.paths;
    // Load background
    if (value.background) {
      this.component.background = value.background;
      if (this.canvas) this.loadBackground(value.background);
    }

    super.setValue(value, { silent: true });
    this.suppressChange = false; // Enable normal change events now
    return true;
  }

  loadBackground(url: string) {
    this.backgroundImage = new Image();
    this.backgroundImage.crossOrigin = 'anonymous';
    this.backgroundImage.onload = () => {
      this.backgroundLoaded = true;

      // Detect image aspect ratio
      const imgW = this.backgroundImage.naturalWidth;
      const imgH = this.backgroundImage.naturalHeight;
      if (imgW && imgH) {
        this.component.aspectRatio = `${imgW}:${imgH}`;
      }

      // Prevent triggering change events
      this.suppressChange = true;
      // Resize canvas to match image aspect ratio
      this.setCanvasWidthAndScale();
      this.redraw();
      this.updateValue(false);
      this.suppressChange = false;
    };

    this.backgroundImage.src = url;
  }

  /** --- Drawing and UI events --- */
  onPointerDown(e: PointerEvent) {
    e.preventDefault();
    this.drawing = true;
    const pos = this.getPos(e);
    this.currentPath = { color: this.colorInput.value, size: Number(this.sizeInput.value), points: [pos] };
    this.paths.push(this.currentPath);
    this.redoStack = [];
  }

  onPointerMove(e: PointerEvent) {
    if (!this.drawing) return;
    const pos = this.getPos(e);
    this.currentPath.points.push(pos);
    this.drawPathSegment(this.currentPath);
  }

  onPointerUp() {
    if (!this.drawing) return;
    this.drawing = false;

    if (this.currentPath) {
      this.undoStack.push(JSON.parse(JSON.stringify(this.paths)));
      this.redoStack = [];
    }

    this.updateValue(true); // user action -> mark isModified
  }

  onUndo() {
    if (this.undoStack.length) {
      this.redoStack.push(JSON.parse(JSON.stringify(this.paths)));
      this.paths = this.undoStack.pop();
      this.redraw();
      this.updateValue(true);
    }
  }

  onRedo() {
    if (this.redoStack.length) {
      this.undoStack.push(JSON.parse(JSON.stringify(this.paths)));
      this.paths = this.redoStack.pop();
      this.redraw();
      this.updateValue(true);
    }
  }

  onClear() {
    if (this.paths.length) {
      this.undoStack.push(JSON.parse(JSON.stringify(this.paths)));
      this.paths = [];
      this.redraw();
      this.updateValue(true);
    }
  }

  onSizeChange(e: Event) {
    const target = e.target as HTMLInputElement;
    this.sizeLabel.textContent = 'Size: ' + target.value;
  }

  /** --- Drawing helpers --- */
  getPos(e: PointerEvent) {
    const rect = this.canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  drawPathSegment(path: any) {
    if (!path || path.points.length < 2) return;
    const a = path.points[path.points.length - 2];
    const b = path.points[path.points.length - 1];
    this.ctx.strokeStyle = path.color;
    this.ctx.lineWidth = path.size;
    this.ctx.lineJoin = 'round';
    this.ctx.lineCap = 'round';
    this.ctx.beginPath();
    this.ctx.moveTo(a.x, a.y);
    this.ctx.lineTo(b.x, b.y);
    this.ctx.stroke();
  }

  clearCanvasKeepBackground() {
    const w = this.canvas.width / (window.devicePixelRatio || 1);
    const h = this.canvas.height / (window.devicePixelRatio || 1);
    this.ctx.clearRect(0, 0, w, h);
    if (this.backgroundLoaded) this.ctx.drawImage(this.backgroundImage, 0, 0, w, h);
  }

  redraw() {
    const w = this.canvas.width / (window.devicePixelRatio || 1);
    const h = this.canvas.height / (window.devicePixelRatio || 1);

    // Clear existing drawing
    this.ctx.clearRect(0, 0, w, h);

    // Draw background image, centered and letterboxed
    if (this.backgroundLoaded && this.backgroundImage) {
      const imgW = this.backgroundImage.naturalWidth;
      const imgH = this.backgroundImage.naturalHeight;
      const imgAspect = imgW / imgH;
      const canvasAspect = w / h;

      let drawW, drawH, offsetX, offsetY;

      if (imgAspect > canvasAspect) {
        // Image is wider than canvas — fit by width
        drawW = w;
        drawH = w / imgAspect;
        offsetX = 0;
        offsetY = (h - drawH) / 2;
      } else {
        // Image is taller than canvas — fit by height
        drawH = h;
        drawW = h * imgAspect;
        offsetX = (w - drawW) / 2;
        offsetY = 0;
      }

      this.ctx.drawImage(this.backgroundImage, offsetX, offsetY, drawW, drawH);
    }

    // Redraw paths on top of background
    for (const path of this.paths) {
      if (!path.points?.length) continue;
      this.ctx.strokeStyle = path.color;
      this.ctx.lineWidth = path.size;
      this.ctx.lineJoin = 'round';
      this.ctx.lineCap = 'round';
      this.ctx.beginPath();
      this.ctx.moveTo(path.points[0].x, path.points[0].y);
      for (let i = 1; i < path.points.length; i++) {
        const p = path.points[i];
        this.ctx.lineTo(p.x, p.y);
      }
      this.ctx.stroke();
    }
  }

  setCanvasWidthAndScale() {
    const ratio = window.devicePixelRatio || 1;
    const containerWidth = this.wrapper.offsetWidth || 600;

    let aspect = 3 / 2;
    // If aspectRatio is numeric (from an image), use it directly
    if (this.component.aspectRatio) {
      const val = this.component.aspectRatio;
      if (typeof val === 'string' && val.includes(':')) {
        const parts = val.split(':');
        if (parts.length === 2) aspect = parseFloat(parts[0]) / parseFloat(parts[1]);
      } else if (!isNaN(Number(val))) {
        aspect = Number(val);
      }
    }
    const displayWidth = Math.min(containerWidth, 800);
    const displayHeight = displayWidth / aspect;

    const storedWidth = this.dataValue?.canvasWidth;
    const storedHeight = this.dataValue?.canvasHeight;

    if (
      storedWidth &&
      storedHeight &&
      this.paths.length > 0 &&
      (storedWidth !== displayWidth || storedHeight !== displayHeight)
    ) {
      const scaleX = displayWidth / storedWidth;
      const scaleY = displayHeight / storedHeight;

      for (const path of this.paths) {
        if (path.points) {
          for (const point of path.points) {
            point.x *= scaleX;
            point.y *= scaleY;
          }
        }
      }

      if (this.dataValue) {
        this.dataValue.canvasWidth = displayWidth;
        this.dataValue.canvasHeight = displayHeight;
      }
    }

    this.canvas.style.width = displayWidth + 'px';
    this.canvas.style.height = displayHeight + 'px';

    this.canvas.width = Math.round(displayWidth * ratio);
    this.canvas.height = Math.round(displayHeight * ratio);
    this.ctx.setTransform(ratio, 0, 0, ratio, 0, 0);

    this.redraw();
  }
}
